import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from "../../../providers/AuthProvider";

/**
 * Internal tool: re-runs a position's AI scoring criteria and every candidate
 * assessment against real data.
 *
 * The run makes a live OpenAI request per candidate and takes minutes, so the
 * request only starts it — progress is then polled from stored state. That
 * keeps the readout honest whether the run finishes, stalls, or dies partway.
 *
 * Temporary, and paired with TestingPositionCriteriaController. Both should go
 * once the assessment flow no longer needs manual re-triggering.
 */

/**
 * Mirrors TestingPositionCriteriaController::AllowedEnvironments.
 *
 * The server is the real gate — it 404s both endpoints anywhere else, and the
 * button hides itself on a 404 regardless. This copy exists so an environment
 * without the tool doesn't fire a status request per position on every brief a
 * staff user opens, purely to be told it isn't there.
 */
const ALLOWED_ENVIRONMENTS = ['local', 'sandbox', 'qa', 'staging', 'production'];

/** Where a rebuild leaves clients staring at an empty score in the meantime. */
const IS_PRODUCTION = () => laravel?.config?.APP_ENV === 'production';

const POLL_INTERVAL_MS = 4000;

// A run that hasn't progressed in this long has almost certainly died; stop
// polling rather than spinning at the user indefinitely.
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

const endpoint = (positionId, action) => TB.api_endpoint(
    `/api/testing/positions/${positionId}/criteria/${action}`
);

/**
 * Reads stored progress, and says which kind of nothing it got back when it
 * fails.
 *
 * The difference matters. The endpoints are route-gated by environment, so a
 * 404 is the tool correctly reporting that it doesn't exist here — the right
 * response to which is to remove the button. A timeout or a 500 is a request
 * that failed, which says nothing about whether the tool is available, and
 * quietly hiding the control for that would look like the feature had gone.
 */
const fetchProgress = async (positionId) => {
    const request = TB.request(endpoint(positionId, 'status'));

    const response = await request.get().catch(() => null);
    const progress = TB.get(response, 'success.data.progress') || null;

    if (progress) {
        return { progress, state: 'ok' };
    }

    const code = request.response?.status
        || request.error?.response?.status
        || 0;

    return {
        progress: null,
        state: TB.includes([401, 403, 404], code) ? 'unavailable' : 'error',
    };
};

/**
 * What the position's stored state says about the last run, in one line.
 *
 * Worth the extra row: the button alone can't distinguish a position whose
 * assessments are current from one that has never been touched, and those want
 * opposite actions. A run costs an OpenAI request per candidate and wipes what
 * is there, so "already done an hour ago" is the single most useful thing to
 * know before pressing it.
 */
const LastRun = ({ progress, checkState, onRetry }) => {
    if (checkState === 'loading') {
        return (
            <span className="text-[11px] leading-none text-[#A2A6AF]">
                Checking…
            </span>
        );
    }

    // Says what failed and offers the only useful next step. Silently showing
    // "never run" here would be a lie with consequences: it's the one state
    // that makes pressing the button look free.
    if (checkState === 'error') {
        return (
            <span className="text-[11px] leading-none text-[#B91C1C]">
                Couldn't check status
                <button
                    type="button"
                    onClick={onRetry}
                    className="ml-1.5 underline"
                >
                    Retry
                </button>
            </span>
        );
    }

    if (!progress?.has_ever_run) {
        return (
            <span className="text-[11px] font-semibold leading-none text-[#B45309]">
                Never run for this role
            </span>
        );
    }

    const when = TB.moment(progress.last_run_at);

    // An incomplete run is the case that most needs calling out: the scores on
    // screen are real but partial, which looks identical to a finished run
    // unless the counts are shown.
    const isPartial = !progress.is_complete;

    return (
        <span
            title={`Last run ${when.format('lll')}`}
            className={TB.mapClass('text-[11px] leading-none', {
                'text-[#B45309]': isPartial,
                'text-[#8B8F9A]': !isPartial,
            })}
        >
            {`Last run ${when.fromNow()}`}
            {progress.total_candidates > 0 ? (
                ` · ${progress.assessed_candidates}/${progress.total_candidates} assessed`
            ) : (
                ' · no proposals to assess'
            )}
        </span>
    );
};

const RefreshPositionAssessmentsButton = ({ position, isDisabled }) => {
    const { auth } = useAuth();

    const [isStarting, setIsStarting] = useState(false);
    const [progress, setProgress] = useState(null);
    const [isRunning, setIsRunning] = useState(false);

    // 'loading' | 'ok' | 'error' | 'unavailable'
    const [checkState, setCheckState] = useState('loading');

    const startedAt = useRef(null);
    const intervalRef = useRef(null);
    const pollCount = useRef(0);
    const isStale = useRef(false);

    const check = async () => {
        setCheckState('loading');

        const result = await fetchProgress(position.id);

        if (isStale.current) {
            return;
        }

        setProgress(result.progress);
        setCheckState(result.state);
    };

    const isEnabledHere = TB.includes(
        ALLOWED_ENVIRONMENTS,
        laravel?.config?.APP_ENV
    );

    useEffect(() => {
        isStale.current = false;

        if (isEnabledHere && auth?.canManageClients()) {
            check();
        }

        return () => { isStale.current = true; };
    }, [position.id, auth?.id, isEnabledHere]);

    const stopPolling = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsRunning(false);
    };

    // Drop the timer if the tab unmounts mid-run. Only the interval is torn
    // down here — touching state on an unmounted component would be pointless.
    useEffect(() => () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    }, []);

    useEffect(() => {
        if (!isRunning) {
            return;
        }

        intervalRef.current = setInterval(async () => {
            const { progress: next } = await fetchProgress(position.id);

            pollCount.current += 1;

            // A failed poll is ignored rather than surfaced: the run is still
            // going regardless, and one dropped request shouldn't replace a
            // live readout with an error.
            if (next) {
                setProgress(next);
                setCheckState('ok');

                // The command clears the old criteria and assessments before
                // rebuilding them. Poll too eagerly and the first read can
                // still show the previous run's finished state, which would
                // stop polling before this run has done anything — so give the
                // wipe a poll to land before trusting completion.
                if (next.is_complete && pollCount.current > 1) {
                    stopPolling();
                    return TB.toast(
                        'Assessments finished. Refresh to see the new scores.',
                        { type: 'success' }
                    );
                }
            }

            if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
                stopPolling();
                TB.toast(
                    'Still running after 15 minutes — check the server log for this position.',
                    { type: 'warning' }
                );
            }
        }, POLL_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isRunning, position.id]);

    if (!isEnabledHere || !auth?.canManageClients() || checkState === 'unavailable') {
        return null;
    }

    const onClick = async () => {
        setIsStarting(true);

        // Re-read rather than trusting the mounted state: the confirmation is
        // about to quote what this destroys, and a page left open while someone
        // else ran it would otherwise quote a count from an hour ago.
        const { progress: current } = await fetchProgress(position.id);

        setIsStarting(false);

        const candidates = current ? (current.total_candidates || 0) : null;

        const cost = candidates === null
            ? 'This wipes any stored criteria and candidate assessments for '
                + `"${position.title}", then rebuilds them — one OpenAI request `
                + 'per proposed candidate, plus one for the criteria.'
            : `This wipes the stored criteria and all ${candidates} candidate `
                + `assessment(s) for "${position.title}", then rebuilds them — `
                + `about ${candidates + 1} OpenAI request(s).`;

        // Said here as well as on the button: this is the last point at which
        // knowing the assessments are an hour old can still change the answer.
        const history = current?.has_ever_run
            ? ` Last run ${TB.moment(current.last_run_at).fromNow()}.`
            : ' This role has never been assessed.';

        // The one consequence that only exists in production: these scores are
        // on screen for the client right now, and the wipe happens immediately
        // while the rebuild takes minutes.
        const liveWarning = IS_PRODUCTION()
            ? ' This is live client-facing data — the client will see no Hire '
                + 'Confidence scores for this role until the rebuild finishes.'
            : '';

        const confirmed = await TB.alert(
            'warning',
            'Re-run Candidate Assessments',
            `${cost}${history}${liveWarning} Continue?`,
            [{ title: 'Yes, Run It' }, { title: 'Cancel' }]
        );

        if (!confirmed) {
            return;
        }

        setIsStarting(true);

        const response = await TB.request(endpoint(position.id, 'run'))
            .post()
            .catch(() => null);

        setIsStarting(false);

        if (!TB.get(response, 'success.data.started')) {
            return TB.toast(
                TB.get(response, 'error.message') || 'Could not start the assessment run.',
                { type: 'error' }
            );
        }

        setProgress(TB.get(response, 'success.data.progress') || null);
        startedAt.current = Date.now();
        pollCount.current = 0;
        setIsRunning(true);

        TB.toast('Assessment run started.', { type: 'success' });
    };

    const label = () => {
        if (isStarting) return 'Starting...';

        if (!isRunning) {
            // Named for what it does from here: there is nothing to re-run on a
            // position that has never been assessed, and calling it a re-run
            // hid exactly the state a reviewer most needs to notice.
            return checkState === 'ok' && !progress?.has_ever_run
                ? 'Run Assessments'
                : 'Re-run Assessments';
        }

        if (!progress?.criteria_count) {
            return 'Generating criteria...';
        }

        return `Assessing ${progress.assessed_candidates} of ${progress.total_candidates}...`;
    };

    // Held shut until the state is known. The run is destructive and costs a
    // live AI request per candidate, and the confirmation can't say what it
    // will destroy without a reading — so an unchecked button is one that can
    // only ask the user to approve something nobody has quantified.
    const isBlocked = checkState !== 'ok';

    return (
        // `relative`, with the status hung out of flow beneath it: in flow it
        // made this column two lines tall while View/Edit and Remove stayed one,
        // and the row's alignment put the three links on three different
        // baselines. Out of flow, the column is exactly the button.
        <span className="relative">
            <button
                type="button"
                className="p-0.5 text-primary-100 underline disabled:opacity-50"
                title="Internal tool: rebuild this position's AI criteria and candidate assessments"
                disabled={isDisabled || isBlocked || isStarting || isRunning}
                onClick={onClick}
            >
                {isRunning && <i className="fas fa-circle-notch fa-spin mr-1.5" />}
                {label()}
            </button>

            {/* Suppressed mid-run, where the button itself is the live readout
                and a stale "last run" beside it would contradict it. */}
            {!isRunning && !isStarting && (
                <span className="absolute right-0 top-full mt-1 whitespace-nowrap">
                    <LastRun
                        progress={progress}
                        checkState={checkState}
                        onRetry={check}
                    />
                </span>
            )}
        </span>
    );
};

export default RefreshPositionAssessmentsButton;
